import { createEffect, createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { Switch } from '../components/Switch';
import { ControlGate, InlineNotice, ScreenHeader } from '../components/ui';
import { audioInputOptions, audioInputPresentation, isRoomInput, type AudioInputDescriptor } from '../lib/audioInputs';
import {
  audioInputClipping,
  audioRenderRateWarning,
  audioTelemetryFromMirror,
  audioTelemetryFromMusicTelemetry,
  audioTelemetryFromStatus,
  dbMeterPercent,
  dbText,
  levelHistoryPath,
  levelHistoryPrerollWidth,
  musicInputStatusLine,
  receivingAudio,
  songChangePresentation,
  tempoPresentation,
  validLevelHistory,
  validLevelHistorySamples,
  type AudioTelemetryFeed,
} from '../lib/audioTelemetry';
import { musicInputCommandInput } from '../lib/effectSync';
import { useMusicTelemetry } from '../state/useMusicTelemetry';
import { useVenue } from '../state/VenueProvider';
import './MusicScreen.css';
import { TEST_IDS, testIdOf } from '../testIds';

type AudioInput = AudioInputDescriptor;
export const DENOISE_MODES = [
  { value: 'filter', label: 'Filter' },
  { value: 'gate', label: 'Gate' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'voice', label: 'Voice' },
] as const;
type DenoiseMode = typeof DENOISE_MODES[number]['value'];

interface AudioReactiveStatus {
  effectId?: string;
  fps?: number;
  outputHz?: number;
  renderRateWarning?: string;
  telemetry?: AudioTelemetryFeed;
}

/**
 * Music is the room's input desk. It never asks the controller on a clock: the hosted console reads the
 * presence-gated audio mirror and direct connections consume the existing audio status notification.
 *
 * The screen states three separate facts and never lets one stand in for another: an input is CHOSEN,
 * the room is LISTENING to it, and sound is ARRIVING. Conflating the last two is what showed an
 * operator a live tempo of 129.7 bpm beside the sentence "Input not receiving audio" — the fallback
 * tracker's number for silence, presented as if the room were hearing music.
 */
export function MusicScreen(): JSX.Element {
  const venue = useVenue();
  // Watching costs the controller bytes, so the lease lives and dies with this screen being on screen.
  useMusicTelemetry();

  const availableInputs = createMemo(() => ((venue.audioInputs().inputs ?? []) as AudioInput[])
    .filter((input) => input.available !== false && input.kind !== 'file' && Boolean(input.identity)));
  // The room's committed choice, as the controller reports it — the same answer every other console and
  // every effect reads. The picker shows THIS, so the screen cannot name an input the room has not got.
  const roomInput = createMemo(() => venue.roomMusicInput());
  const pickerOptions = createMemo(() => audioInputOptions(availableInputs(), roomInput()));
  const selectedInput = createMemo(() => availableInputs()
    .find((input) => input.identity === venue.selectedAudioInputIdentity()));
  const selectedInputDetails = createMemo(() => selectedInput() ? audioInputPresentation(selectedInput()!) : undefined);
  const directFeed = createMemo(() => venue.controlTransport() === 'lan' || venue.controlTransport() === 'bluetooth');
  const mirroredEffect = createMemo(() => venue.mirror()?.effect);
  const liveAudioEffect = createMemo(() => venue.controllerState()?.effects?.find((effect) => effect.kind === 'audio_reactive'));
  const effectId = createMemo(() => {
    const directId = liveAudioEffect()?.id ?? liveAudioEffect()?.effectId ?? '';
    const mirrorId = mirroredEffect()?.running && mirroredEffect()?.kind === 'audio_reactive'
      ? mirroredEffect()?.effectId ?? ''
      : '';
    // A warmed cloud mirror may remain in memory during venue-network/Bluetooth fallback. Direct state
    // is authoritative there; hosted control continues to prefer the presence-gated mirror.
    return String(directFeed() ? directId : mirrorId || directId);
  });
  const status = createMemo<AudioReactiveStatus | undefined>(() => {
    const current = venue.audioStatus() as AudioReactiveStatus | undefined;
    return effectId() && current?.effectId === effectId() ? current : undefined;
  });
  const statusTelemetry = createMemo(() => audioTelemetryFromStatus(status()));
  /**
   * What the room is hearing, as the transport in use can report it.
   *
   * A direct console reads the daemon's pushed `audioMonitor` block. A hosted one reads the leased
   * `musicTelemetry` node, which is the only cloud source of a tempo there is: controller 1.7.20 retired
   * `audioMonitor` from the mirror along with every other moving measurement, and from that release until
   * 1.7.21 this screen had nothing to read — so a controller locked at 146 bpm drew a blank card, silently.
   *
   * It is the room's ear either way, whether or not an effect happens to be using it: the effect borrows
   * the same capture, so there is no second reading to prefer.
   */
  const monitorTelemetry = createMemo<AudioTelemetryFeed | undefined>(() => (
    directFeed()
      ? audioTelemetryFromMirror(venue.lanStatus()?.audioMonitor)
      : audioTelemetryFromMusicTelemetry(venue.mirror()?.musicTelemetry)
  ));
  const mirrorTelemetry = createMemo(() => monitorTelemetry());
  const telemetry = createMemo<AudioTelemetryFeed | undefined>(() => (
    effectId() && directFeed() ? statusTelemetry() : mirrorTelemetry()
  ));
  /**
   * The feed for the ROOM's input. A capture someone else aimed at a different device says nothing
   * about this room's music, so it is not shown as if it did.
   */
  const roomTelemetry = createMemo<AudioTelemetryFeed | undefined>(() => {
    const feed = telemetry();
    const room = String(roomInput()?.id ?? '');
    if (!feed || !room) return undefined;
    const reported = String(feed.inputId ?? '');
    if (!reported) return feed;
    return reported === room || isRoomInput(venue.selectedAudioInputIdentity(), reported) ? feed : undefined;
  });
  const channelIndexes = createMemo(() => Array.from({ length: Math.min(8, Math.max(
    Number(selectedInput()?.channels ?? 0),
    roomTelemetry()?.channels?.length ?? 0,
    roomInput()?.channelGainDb?.length ?? 0,
    roomInput()?.channelDenoise?.length ?? 0,
    roomInput()?.channelDenoiseMode?.length ?? 0,
  )) }, (_, index) => index));
  const [gainIntent, setGainIntent] = createSignal<Record<number, number>>({});
  const [gainDraft, setGainDraft] = createSignal<Record<number, string>>({});
  const [denoiseIntent, setDenoiseIntent] = createSignal<Record<number, boolean>>({});
  const [denoiseModeIntent, setDenoiseModeIntent] = createSignal<Record<number, DenoiseMode>>({});
  function channelDenoiseMode(channel: number): string {
    return denoiseModeIntent()[channel] ?? roomInput()?.channelDenoiseMode?.[channel] ?? 'filter';
  }
  createEffect(() => {
    const gains = roomInput()?.channelGainDb ?? [];
    setGainIntent((current) => Object.fromEntries(Object.entries(current)
      .filter(([channel, value]) => gains[Number(channel)] !== value)));
    const denoise = roomInput()?.channelDenoise ?? [];
    setDenoiseIntent((current) => Object.fromEntries(Object.entries(current)
      .filter(([channel, value]) => denoise[Number(channel)] !== value)));
    const modes = roomInput()?.channelDenoiseMode ?? [];
    setDenoiseModeIntent((current) => Object.fromEntries(Object.entries(current)
      .filter(([channel, value]) => modes[Number(channel)] !== value)));
  });
  async function setChannelGain(channel: number, gainDb: number, field: HTMLInputElement): Promise<void> {
    if (!Number.isFinite(gainDb) || gainDb < -24 || gainDb > 24) {
      field.value = String(roomInput()?.channelGainDb?.[channel] ?? 0);
      setGainDraft((current) => ({ ...current, [channel]: field.value }));
      setGainIntent((current) => {
        const next = { ...current }; delete next[channel]; return next;
      });
      return;
    }
    setGainIntent((current) => ({ ...current, [channel]: gainDb }));
    const applied = await venue.runAction(`set-audio-input-gain-${channel}`, async () => {
      await venue.controllerCall('set_audio_input_gain', { channel, gainDb });
      return true;
    });
    if (!applied) setGainIntent((current) => {
      const next = { ...current }; delete next[channel]; return next;
    });
  }
  function clearGainDraft(channel: number): void {
    setGainDraft((current) => {
      const next = { ...current }; delete next[channel]; return next;
    });
  }
  async function setChannelDenoise(channel: number, enabled: boolean): Promise<void> {
    setDenoiseIntent((current) => ({ ...current, [channel]: enabled }));
    const applied = await venue.runAction(`set-audio-input-denoise-${channel}`, async () => {
      await venue.controllerCall('set_audio_input_denoise', { channel, enabled });
      return true;
    });
    if (!applied) setDenoiseIntent((current) => {
      const next = { ...current }; delete next[channel]; return next;
    });
  }
  async function setChannelDenoiseMode(channel: number, mode: DenoiseMode): Promise<void> {
    if (channelDenoiseMode(channel) === mode) return;
    setDenoiseModeIntent((current) => ({ ...current, [channel]: mode }));
    const applied = await venue.runAction(`set-audio-input-denoise-${channel}`, async () => {
      await venue.controllerCall('set_audio_input_denoise', { channel, enabled: true, mode });
      return true;
    });
    if (!applied) setDenoiseModeIntent((current) => {
      const next = { ...current }; delete next[channel]; return next;
    });
  }

  const listening = createMemo(() => venue.roomMusicListening());
  /**
   * Where the operator just put the switch, until the room agrees or refuses.
   *
   * The room's own listening state is the truth; this only covers the round trip, so the switch is
   * never a control that sits still under a finger while a command travels.
   */
  const [listeningIntent, setListeningIntent] = createSignal<boolean>();
  const listeningShown = createMemo(() => listeningIntent() ?? listening());
  createEffect(() => {
    if (listeningIntent() !== undefined && listening() === listeningIntent()) setListeningIntent(undefined);
  });
  /** Sound is arriving — the analyzer's own verdict, and only while the room is listening for one. */
  const receiving = createMemo(() => listening() && receivingAudio(roomTelemetry()?.inputSignal) === true);
  const statusLine = createMemo(() => musicInputStatusLine({
    roomHasInput: Boolean(roomInput()),
    inputSignal: roomTelemetry()?.inputSignal,
  }));
  const levelHistory = createMemo(() => validLevelHistory(roomTelemetry()?.levelHistory));
  const historySamples = createMemo(() => validLevelHistorySamples(roomTelemetry()?.levelHistorySamples, levelHistory()));
  const peakHistoryPath = createMemo(() => levelHistoryPath(levelHistory(), 0, 320, 84, historySamples()));
  const rmsHistoryPath = createMemo(() => levelHistoryPath(levelHistory(), 1, 320, 84, historySamples()));
  const historyPrerollWidth = createMemo(() => levelHistoryPrerollWidth(historySamples()));
  const clipping = createMemo(() => audioInputClipping(roomTelemetry()));
  const renderRateWarning = createMemo(() => audioRenderRateWarning(
    roomTelemetry(),
    directFeed() ? String(status()?.renderRateWarning ?? '') : '',
  ));

  /**
   * Re-assert the room's input on the element after its options are rebuilt.
   *
   * A `<select>` whose options are replaced loses its selection: the browser falls back to the first
   * entry, and the `value` binding does not fire again because the SIGNAL did not change. Every refresh
   * of the controller's input list produces new option nodes, so without this the picker showed the
   * room's input for one beat and then went blank — the same lie this screen exists to stop telling.
   */
  let picker!: HTMLSelectElement;
  createEffect(() => {
    pickerOptions();
    const identity = venue.selectedAudioInputIdentity();
    if (picker && picker.value !== identity) picker.value = identity;
  });

  /**
   * Committing the room's music input. This is the one place the choice is made: it is stored on the
   * controller, survives a restart, and re-aims every effect that already has "Sync to music" on — so
   * an operator never has to go back and re-toggle switches after fixing the input. The picker shows the
   * operator's choice immediately and falls back to the room's own value if the controller refuses it.
   */
  async function chooseInput(identity: string): Promise<void> {
    venue.selectAudioInput(identity);
    if (!identity) return;
    // Re-choosing what the room already listens to asks the controller for nothing. This is also the
    // entry that stands in for an input the controller has stopped reporting, which nothing can commit.
    if (isRoomInput(identity, String(roomInput()?.id ?? ''))) return;
    const input = musicInputCommandInput(availableInputs().find((candidate) => candidate.identity === identity));
    if (!input) {
      venue.selectAudioInput('');
      return;
    }
    const result = await venue.runAction(
      'set-music-input',
      () => venue.controllerCall('set_music_input', { input }),
      'Music input saved for this room',
    );
    if (result === undefined) venue.selectAudioInput('');
  }

  /**
   * Turning the room's ear on and off. It is a ROOM state the controller keeps, not a console session:
   * every console agrees about it, it survives a restart, and it leaves the chosen input chosen — an
   * operator who stops listening for a soundcheck does not have to pick their interface again after.
   */
  async function setListening(enabled: boolean): Promise<void> {
    // Beat one: the switch moves where the operator put it, now, before anything is sent. Beat two is
    // the spinner beside it; the room's own state takes the switch back as soon as it agrees, and a
    // refusal hands it straight back with the reason in the feedback slot.
    setListeningIntent(enabled);
    const applied = await venue.runAction(
      'set-music-listening',
      async () => {
        await venue.controllerCall('set_music_listening', { enabled });
        return true;
      },
    );
    if (!applied) setListeningIntent(undefined);
  }

  return (
    <>
      <ScreenHeader title="Music" description="What the controller is hearing." />
      <ControlGate>
        <div class="music-layout">
          <section class="surface music-source">
            <div class="section-heading">
              <div><h2>Music input</h2></div>
            </div>

            <label class="music-input-picker">
              <span>Input</span>
              <select
                ref={picker}
                value={venue.selectedAudioInputIdentity()}
                disabled={Boolean(effectId())}
                onChange={(event) => void chooseInput(event.currentTarget.value)}
                data-testid={TEST_IDS.audioInput}
              >
                <option value="">Choose a music input</option>
                <For each={pickerOptions()}>
                  {(option) => <option value={option.identity}>{option.label}</option>}
                </For>
              </select>
            </label>
            <Show when={selectedInputDetails()?.rawName}>
              {(raw) => <p class="field-help music-input-raw" data-testid={TEST_IDS.audioInputRaw}>{raw()}</p>}
            </Show>

            <Show when={!effectId() && roomInput()}>
              <div class="music-listening-control">
                <span>Listening</span>
                <Switch
                  checked={listeningShown()}
                  busy={venue.actionBusy() === 'set-music-listening'}
                  disabled={!venue.controlsEnabled() || venue.actionBusy() === 'set-music-listening'}
                  label="Listening"
                  testId={TEST_IDS.musicListening}
                  onChange={(checked) => void setListening(checked)}
                />
              </div>
            </Show>
            <Show when={effectId()}>
              <p class="music-effect-listening"><i aria-hidden="true" /> Audio reactive is using this input now.</p>
            </Show>
            <Show when={roomInput() && channelIndexes().length > 0}>
              <div class="music-channel-controls">
                <For each={channelIndexes()}>{(channel) => (
                  <div class="music-channel-control">
                    <span>Channel {channel + 1}</span>
                    <label>
                      <span>Gain</span>
                      <input type="number" min="-24" max="24" step="0.1"
                        value={gainDraft()[channel] ?? gainIntent()[channel] ?? roomInput()?.channelGainDb?.[channel] ?? 0}
                        disabled={!venue.controlsEnabled() || venue.actionBusy() === `set-audio-input-gain-${channel}`}
                        aria-label={`Channel ${channel + 1} gain`}
                        data-testid={testIdOf('musicChannelGain', { channel: channel + 1 })}
                        onFocus={(event) => setGainDraft((current) => ({ ...current, [channel]: event.currentTarget.value }))}
                        onInput={(event) => setGainDraft((current) => ({ ...current, [channel]: event.currentTarget.value }))}
                        onBlur={() => clearGainDraft(channel)}
                        onChange={(event) => void setChannelGain(channel, event.currentTarget.valueAsNumber, event.currentTarget)} />
                    </label>
                    <label class="music-channel-denoise">
                      <span>Denoise</span>
                      <Switch checked={denoiseIntent()[channel] ?? roomInput()?.channelDenoise?.[channel] ?? false}
                        disabled={!venue.controlsEnabled() || venue.actionBusy() === `set-audio-input-denoise-${channel}`}
                        label={`Channel ${channel + 1} denoise`}
                        testId={testIdOf('musicChannelDenoise', { channel: channel + 1 })}
                        onChange={(enabled) => void setChannelDenoise(channel, enabled)} />
                    </label>
                    <Show when={denoiseIntent()[channel] ?? roomInput()?.channelDenoise?.[channel] ?? false}>
                      <div class="music-channel-denoise-modes" role="radiogroup" aria-label={`Channel ${channel + 1} denoise mode`}>
                        <For each={DENOISE_MODES}>{({ value, label }) => (
                          <button type="button" role="radio"
                            classList={{ selected: channelDenoiseMode(channel) === value }}
                            aria-checked={channelDenoiseMode(channel) === value}
                            disabled={!venue.controlsEnabled() || venue.actionBusy() === `set-audio-input-denoise-${channel}`}
                            onClick={() => void setChannelDenoiseMode(channel, value)}>{label}</button>
                        )}</For>
                      </div>
                    </Show>
                  </div>
                )}</For>
              </div>
            </Show>
          </section>

          <section class="surface music-analysis" data-testid={TEST_IDS.musicTelemetry}>
            <div class="section-heading">
              <div><h2>Tempo &amp; level</h2></div>
            </div>

            <p class="music-input-state" data-testid={TEST_IDS.musicInputState}>{statusLine()}</p>

            {/*
              * Below the state line, this card belongs to a room that is LISTENING. A room with no input,
              * or one whose ear is switched off, has no levels to draw and no tempo to name, and drawing
              * them anyway is the screen claiming to hear something it is not listening to.
              */}
            <Show when={listening()}>
              <Show when={renderRateWarning()}>
                {(warning) => <InlineNotice tone="warning"><span data-testid={TEST_IDS.audioRenderRateWarning}>{warning()}</span></InlineNotice>}
              </Show>

              {/*
                * The tempo number waits for sound. The analyzer always has a number — a free-running
                * fallback tracker keeps one through silence — and printing that number beside "Input not
                * receiving audio" is noise dressed as a measurement.
                */}
              <Show when={receiving()}>
                <div class="music-tempo-readout">
                  <div class="music-tempo-value">
                    <small>Tempo</small>
                    {/*
                      * The mark says the analyzer is LOCKED onto this number. Without it the same
                      * 119.8 bpm is drawn whether the room is on a click track or the fallback tracker
                      * is guessing at silence, which is a confident-wrong readout. Not locked, or a
                      * controller that does not report it, shows nothing here.
                      */}
                    <div class="music-tempo-line">
                      <strong data-testid={TEST_IDS.audioTempo}>{tempoPresentation(roomTelemetry())}</strong>
                      <Show when={roomTelemetry()?.locked === true}>
                        <span class="music-tempo-locked" data-testid={TEST_IDS.audioTempoLocked}>locked</span>
                      </Show>
                    </div>
                    <Show when={songChangePresentation(roomTelemetry())}>
                      {(label) => <span data-testid={TEST_IDS.songChangeAge}>{label()}</span>}
                    </Show>
                  </div>
                </div>
              </Show>

              <Show when={roomTelemetry()}>
                <div class="audio-input-instrument" data-testid={TEST_IDS.audioInputInstrument}>
                  <div class="audio-db-meters">
                    <Show when={clipping()}>
                      <span class="telemetry-clip" data-testid={TEST_IDS.audioInputClipping}>Clipping — lower input gain</span>
                    </Show>
                    <div class="audio-db-row">
                      <span>Peak</span>
                      <div
                        class="audio-db-track"
                        role="meter"
                        aria-label="Peak input level"
                        aria-valuemin="-90"
                        aria-valuemax="0"
                        aria-valuenow={roomTelemetry()?.peakDb}
                      ><i style={{ width: `${dbMeterPercent(roomTelemetry()?.peakDb)}%` }} /></div>
                      <strong data-testid={TEST_IDS.audioPeakDb}>{dbText(roomTelemetry()?.peakDb)}</strong>
                    </div>
                    <div class="audio-db-row rms">
                      <span>RMS</span>
                      <div
                        class="audio-db-track"
                        role="meter"
                        aria-label="RMS input level"
                        aria-valuemin="-90"
                        aria-valuemax="0"
                        aria-valuenow={roomTelemetry()?.rmsDb}
                      ><i style={{ width: `${dbMeterPercent(roomTelemetry()?.rmsDb)}%` }} /></div>
                      <strong data-testid={TEST_IDS.audioRmsDb}>{dbText(roomTelemetry()?.rmsDb)}</strong>
                    </div>
                    <div class="audio-db-scale" aria-hidden="true"><span>−90</span><span>−60</span><span>−30</span><span>0 dBFS</span></div>
                    <For each={roomTelemetry()?.channels ?? []}>{(channel, index) => (
                      <div class="music-channel-meter" classList={{ clipped: channel.clipped }}
                        data-testid={testIdOf('musicChannelMeter', { channel: index() + 1 })}>
                        <span>Channel {index() + 1}</span>
                        <div class="audio-db-row">
                          <span>Peak</span>
                          <div class="audio-db-track" role="meter" aria-label={`Channel ${index() + 1} peak input level`}
                            aria-valuemin="-90" aria-valuemax="0" aria-valuenow={channel.peakDb}>
                            <i style={{ width: `${dbMeterPercent(channel.peakDb)}%` }} />
                          </div>
                          <strong>{dbText(channel.peakDb)}</strong>
                        </div>
                        <div class="audio-db-row rms">
                          <span>RMS</span>
                          <div class="audio-db-track" role="meter" aria-label={`Channel ${index() + 1} RMS input level`}
                            aria-valuemin="-90" aria-valuemax="0" aria-valuenow={channel.rmsDb}>
                            <i style={{ width: `${dbMeterPercent(channel.rmsDb)}%` }} />
                          </div>
                          <strong>{dbText(channel.rmsDb)}</strong>
                        </div>
                      </div>
                    )}</For>
                  </div>

                  {/*
                    * The graph draws only where there is a history to draw. The leased cloud channel
                    * carries the two meters and not forty seconds of pairs — that would cost eighteen
                    * times the rest of the channel — so a hosted console gets the meters and no graph
                    * rather than an empty frame with a label on it.
                    */}
                  <Show when={levelHistory().length > 0}>
                  <div class="audio-level-history">
                    <div><span>Last 10 seconds</span><small>Peak + RMS · dBFS</small></div>
                    <svg
                      viewBox="0 0 320 84"
                      preserveAspectRatio="none"
                      role="img"
                      aria-label="Peak and RMS audio levels over the last ten seconds"
                      data-testid={TEST_IDS.audioLevelHistory}
                    >
                      <defs>
                        <pattern id="music-history-preroll" width="8" height="8" patternUnits="userSpaceOnUse">
                          <path d="M-2,2 L2,-2 M0,8 L8,0 M6,10 L10,6" />
                        </pattern>
                      </defs>
                      <rect
                        class="history-preroll music-history-preroll"
                        width={historyPrerollWidth()}
                        height="84"
                        data-testid={TEST_IDS.audioHistoryPreroll}
                      />
                      <g aria-hidden="true"><line x1="0" y1="0" x2="320" y2="0" /><line x1="0" y1="28" x2="320" y2="28" /><line x1="0" y1="56" x2="320" y2="56" /><line x1="0" y1="84" x2="320" y2="84" /></g>
                      <path class="history-rms" d={rmsHistoryPath()} />
                      <path class="history-peak" d={peakHistoryPath()} />
                    </svg>
                    <div class="audio-history-legend"><span class="peak">Peak</span><span class="rms">RMS</span></div>
                  </div>
                  </Show>
                </div>
              </Show>
            </Show>
          </section>
        </div>
      </ControlGate>
    </>
  );
}
